import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import * as S from './ReceivementPurchase.styles'
import * as selectors from '../../ReceivementSelectors'
import { actions } from '../../ReceivementState'
import { ReceivementHeader } from '../../components/ReceivementHeader'
import { ReceivementInfo } from '../../components/ReceivementInfo'
import { ReceivementPurchaseFilters } from '../../components/ReceivementPurchaseFilters'
import { Watch } from './Watch'
import { useSelector, useStore } from 'src/utils/store'
import { ProductsList } from './components/ProductsList'
import { ssoClient } from 'src/services/SigninSetup'
import { SupplierInfo } from '../../components/SupplierInfo/SupplierInfo'
import { InfoReturnOptions, OverviewProductStatusEnum } from '../../ReceivementTypes'

const ReceivementPurchase = () => {
  const dispatch = useDispatch()
  const { receivementId } = useParams<{ receivementId?: string }>()
  const store = useStore()
  const salesOverview = useSelector(selectors.selectSalesOverview)
  const awaitStatus = useSelector(selectors.selectSalesAwaitStatus)
  const userAssigned = useSelector(selectors.selectUserAssigned)
  const loading = useSelector(selectors.selectReceivementLoading)
  const salesHasErrors = useSelector(selectors.selectSalesHasErrors)
  const isOneNotReady = useSelector(selectors.selectIsOneNotReady)
  const history = useHistory()
  const [authorized, setAuthorized] = useState(false)
  const [invoiceReturnOptions, setInvoiceReturnOptions] = useState<InfoReturnOptions>()
  const [
    invalidAcceptedQuantity,
    setInvalidAcceptedQuantity,
  ] = useState(false)

  React.useEffect(() => {
    dispatch(actions.clear())
  }, [dispatch])

  React.useEffect(() => {
    if (salesOverview) {
      const quantityAcceptedIsNotValid = salesOverview.products.some((p) =>
        p.status !== OverviewProductStatusEnum.WRONG && !p.quantityAcceptedIsValid)

      setInvalidAcceptedQuantity(
        quantityAcceptedIsNotValid,
      )
    }
  }, [salesOverview])

  useEffect(() => {
    if (salesOverview) {
      const {
        allowBreakUnitOnInvoiceReturn,
        allowInvoiceReturn,
        allowPartialReturn,
        allowProductExchange,
      } = salesOverview

      setInvoiceReturnOptions({
        allowBreakUnitOnInvoiceReturn, allowInvoiceReturn, allowPartialReturn, allowProductExchange,
      })
    }
  }, [salesOverview])

  const isFinishButtonDisabled = React.useCallback(() => {
    if (loading) {
      return true
    }

    if (salesOverview === undefined) {
      return true
    }

    return isOneNotReady || salesHasErrors || invalidAcceptedQuantity
  }, [
    salesOverview,
    salesHasErrors,
    isOneNotReady,
    loading,
    invalidAcceptedQuantity,
  ])

  const handleClearUserAssigned = React.useCallback(() => {
    dispatch(actions.clearUserAssigned({ receivementId }))
    history.push('/')
  }, [receivementId, dispatch, history])

  useEffect(() => {
    ssoClient
      .checkClaim('receivement:reset_buyer')
      .then((authorization) => setAuthorized(authorization))
      .catch(() => setAuthorized(false))
  }, [])

  const handleSalesFinish = React.useCallback(async () => {
    if (!receivementId) return

    const { batchesStateByBatchItemOrderIds } = store.getState().receivement
    dispatch(actions.closeSalesAdjustment({ receivementId, data: Object.values(batchesStateByBatchItemOrderIds), history }))
  }, [dispatch, receivementId, store, history])

  const headerButton = React.useMemo(
    () => (
      <S.ButtonWrapper>
        {authorized && (
        <S.Button onClick={handleClearUserAssigned}>
          Desvincular comprador
        </S.Button>
        )}
        <S.Button onClick={handleSalesFinish} disabled={isFinishButtonDisabled()}>
          Finalizar
        </S.Button>
      </S.ButtonWrapper>
    ),
    [handleClearUserAssigned, isFinishButtonDisabled, handleSalesFinish, authorized],
  )

  if (userAssigned) {
    return (
      <S.ContainerReset>
        <S.Title>Ajuste de compras iniciado por outra pessoa!</S.Title>
        <S.Title>Comprador(a): {userAssigned}</S.Title>
        {authorized
        && (
        <S.ButtonReset onClick={handleClearUserAssigned}>
          Desvincular comprador
        </S.ButtonReset>
        )}
      </S.ContainerReset>
    )
  }

  return (
    <>
      {awaitStatus ? (
        <Watch />
      ) : (
        <S.Container>
          <ReceivementHeader
            title={<S.SubTitle>Relatório de Aceite</S.SubTitle>}
            headerButton={headerButton}
          />
          <S.ContainerInfo>
            <ReceivementInfo
              operator={salesOverview?.operator}
              supervisor={salesOverview?.supervisor}
              dock={salesOverview?.dock}
              supplier={salesOverview?.supplier}
              receivementId={receivementId}
            />
            {
              invoiceReturnOptions && <SupplierInfo info={invoiceReturnOptions} withSwapInfo/>
            }
          </S.ContainerInfo>
          <ReceivementPurchaseFilters />
          <ProductsList/>
        </S.Container>
      )}
    </>
  )
}

export default ReceivementPurchase
